import './PackageItemsList.scss';

import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useResourceParams } from '@refinedev/core';
import { Button, Empty, Flex, Modal, Skeleton, Table } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUiContext } from '@/contexts/UiContext';
import { Package } from '@/model/Logistic/Package';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { ProductStatusStrings } from '@/model/Logistic/PackageItem/ProductStatus';
import {
  SHIPPING_ITEM_REFERENCE,
  ShippingItemReferenceString,
} from '@/model/Logistic/Shipment/ShipmentReference';
import { RESOURCES } from '@/Resources';

import PackageItemForm from './PackageItemForm/PackageItemForm';
import PackageItemShow from './PackageItemShow/PackageItemShow';
import usePackageItemsList from './usePackageItemsList';

interface PackageItemsListProps {
  packageItems: PackageItem[];
  setPackageItems: (items: PackageItem[]) => void;
  package?: Partial<Package>;
  editMode: 'enabled' | 'partial' | 'partial-with-news' | 'disabled';
  setBlockChangeState?: Function;
  refetchResource?: Function;
  isRefetchingResource?: boolean;
  canEditResolution?: boolean;
}

const PackageItemsList = (props: PackageItemsListProps) => {
  const { t } = useTranslation();
  const { isMediumScreen } = useUiContext();
  const {
    packageItems,
    setPackageItems,
    package: pkg = {},
    editMode,
    setBlockChangeState,
    refetchResource,
    canEditResolution,
  } = props;
  const { id: packageId, movil_link } = pkg;
  const [openModal, setOpenModal] = useState(false);
  const [showEditFooter, setShowEditFooter] = useState(false);
  const [editingPackageItem, setEditingPackageItem] = useState<PackageItem | undefined>();
  // Action de la ruta
  const { resource, action } = useResourceParams();
  const showResource = action === 'show';
  const isCollectionShow = useMemo(
    () => resource?.name === RESOURCES.SPARE_PARTS_COLLECTIONS,
    [resource],
  );
  const isShipment = useMemo(() => resource?.name === RESOURCES.SHIPMENTS, [resource]);
  const isReception = useMemo(() => resource?.name === RESOURCES.RECEPTIONS, [resource]);
  const itemsToShow = useMemo(
    () =>
      packageItems.filter(
        i =>
          !i.toDelete &&
          (!isCollectionShow ||
            i.item_shipped_reference === SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO),
      ),
    [isCollectionShow, packageItems],
  );
  const closeModal = useCallback(() => {
    setOpenModal(false);
    setEditingPackageItem(undefined);
  }, []);
  const { isLoading, isFetching, refetch, handleDelete, saveItems } = usePackageItemsList(
    packageItems,
    setPackageItems,
    packageId,
    showEditFooter,
  );

  useEffect(() => {
    if (showEditFooter) {
      setBlockChangeState?.(prevState =>
        prevState.includes(bs => bs.id == 'shipment-package-items')
          ? prevState
          : [
              ...prevState,
              {
                id: 'shipment-package-items',
                message: t('Hay cambios sin guardar en los elementos del paquete'),
              },
            ],
      );
    } else {
      setBlockChangeState?.(prevState => prevState.filter(bs => bs.id != 'shipment-package-items'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditFooter, t]);

  const refetchPackageItems = useCallback(() => {
    void refetch().then(({ data }) => {
      if (data) {
        setPackageItems(data.data);
      }
    });
  }, [refetch, setPackageItems]);

  useEffect(() => {
    if (props.isRefetchingResource) {
      refetchPackageItems();
    }
  }, [props.isRefetchingResource, refetchPackageItems]);

  const showRelatedQuantities = useMemo(() => isReception, [isReception]);

  return (
    <div className="PackageItemsList">
      {!!packageId && (isLoading || isFetching) ? (
        <Skeleton />
      ) : (
        <Table<PackageItem>
          tableLayout="fixed"
          rowKey={record =>
            record.id
              ? record.id.toString()
              : [record.name ?? '', record.device ?? '', record.unidad ?? ''].join('-') ||
                `item-${Math.random()}`
          }
          dataSource={itemsToShow.sort((a, b) => {
            if (a.id === undefined && b.id === undefined) return 0;
            if (a.id === undefined) return 1;
            if (b.id === undefined) return -1;

            return a.id - b.id;
          })}
          locale={{ emptyText: <Empty description={t('No hay elementos cargados')} /> }}
          footer={() => (
            <div className="PackageItemsListButtons">
              {showEditFooter && (
                <>
                  <Button
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setShowEditFooter(false);

                      void refetch().then(({ data }) => {
                        if (data) {
                          setPackageItems(data.data);
                        }
                      });
                    }}
                    size={isMediumScreen ? 'small' : 'middle'}>
                    {t('Cancelar')}
                  </Button>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setOpenModal(true)}
                    size={isMediumScreen ? 'small' : 'middle'}>
                    {t('Cargar elemento')}
                  </Button>
                  <Button
                    icon={<SaveOutlined />}
                    type="primary"
                    onClick={() =>
                      Modal.confirm({
                        title: t('¿Estás seguro de que deseas guardar los cambios?'),
                        content: t('Esta acción no se puede deshacer'),
                        onOk: () =>
                          void saveItems().then(() => {
                            if (refetchResource) {
                              void refetchResource();
                            }

                            setShowEditFooter(false);
                          }),
                      })
                    }
                    size={isMediumScreen ? 'small' : 'middle'}>
                    {t('Guardar cambios')}
                  </Button>
                </>
              )}
              {(editMode == 'enabled' || editMode == 'partial-with-news') && !showEditFooter && (
                <Button
                  icon={showResource ? <EditOutlined /> : <PlusOutlined />}
                  onClick={() => {
                    if (showResource) {
                      setShowEditFooter(true);
                    } else {
                      setOpenModal(true);
                    }
                  }}
                  size={isMediumScreen ? 'small' : 'middle'}>
                  {showResource ? t('Editar elementos') : t('Cargar elemento')}
                </Button>
              )}
            </div>
          )}
          pagination={false}
          scroll={{ x: 1400 }}>
          <Table.Column
            width={200}
            title={t('Elemento')}
            render={(item: PackageItem) => item.name}
          />
          <Table.Column
            width={130}
            title={t('Número de serie')}
            render={(item: PackageItem) => item.serial_number ?? '-'}
          />
          {(isShipment || isCollectionShow) && (
            <Table.Column
              width={80}
              title={t('Estado')}
              render={(item: PackageItem) =>
                item.item_shipped_state != undefined
                  ? t(ProductStatusStrings[item.item_shipped_state])
                  : '-'
              }
            />
          )}

          <Table.Column
            width={70}
            dataIndex={'requested_amount'}
            title={
              isShipment || isCollectionShow
                ? t('Cant. solicitada')
                : showRelatedQuantities
                  ? t('Cant. enviada')
                  : t('Cant. a recibir')
            }
          />
          {showRelatedQuantities && (
            <>
              <Table.Column
                width={70}
                dataIndex={'used_amount'}
                title={t('Cant. utilizada')}
                render={v => v ?? 0}
              />
              <Table.Column
                width={100}
                dataIndex={'technician_stock_amount'}
                title={t('Cant. a stock técnico')}
                render={v => v ?? 0}
              />
              <Table.Column
                width={80}
                dataIndex={'discarded_amount'}
                title={t('Cant. descartada')}
                render={v => v ?? 0}
              />
              <Table.Column
                width={80}
                dataIndex={'to_return_amount'}
                title={t('Cant. a recibir')}
                render={v => v ?? 0}
              />
            </>
          )}
          {!isCollectionShow && (
            <>
              <Table.Column
                width={70}
                dataIndex={'execute_amount'}
                title={isShipment ? t('Cant. enviada') : t('Cant. recibida')}
                render={v => v ?? 0}
              />
              <Table.Column
                width={65}
                dataIndex={'in_warranty_period'}
                title={t('En garantía')}
                render={v => (
                  <Flex align="center" justify="center">
                    {v ? (
                      <CheckOutlined className="PackageItemsListCheckIcon" />
                    ) : (
                      <CloseOutlined className="PackageItemsListCloseIcon" />
                    )}
                  </Flex>
                )}
              />
            </>
          )}

          {isShipment && (
            <Table.Column
              width={100}
              dataIndex={'item_shipped_reference'}
              title={t('Referencia')}
              render={v => (v != undefined ? t(ShippingItemReferenceString[v]) : '-')}
            />
          )}

          <Table.Column
            fixed="right"
            width={60}
            render={(_, record) => (
              <div className="ShipmentPackageItemActions">
                <Button
                  size="small"
                  icon={editMode == 'enabled' || !record?.id ? <EditOutlined /> : <EyeOutlined />}
                  onClick={() => {
                    setEditingPackageItem(record as PackageItem);
                    setOpenModal(true);
                    if (showResource && editMode == 'enabled' && !showEditFooter) {
                      setShowEditFooter(true);
                    }
                  }}
                />

                {(editMode == 'enabled' || !record?.id) && (
                  <Button
                    className="ShipmentPackageItemActionsDeleteButton"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      Modal.confirm({
                        title: t('Confirmar eliminación'),
                        content: t('¿Está seguro de que desea eliminar este elemento?'),
                        onOk: () => {
                          handleDelete(record);
                          if (showResource && !showEditFooter) {
                            setShowEditFooter(true);
                          }
                        },
                      });
                    }}
                  />
                )}
              </div>
            )}
          />
        </Table>
      )}

      <Modal
        open={openModal}
        onCancel={closeModal}
        width={700}
        footer={null}
        maskClosable={false}
        destroyOnHidden={true}
        centered>
        {editMode != 'enabled' && editingPackageItem?.id ? (
          <PackageItemShow
            packageItems={packageItems}
            setPackageItems={setPackageItems}
            closeModal={closeModal}
            editingPackageItem={editingPackageItem}
            editEnabled={editMode != 'disabled'}
            refetchPackageItems={refetchPackageItems}
            canEditResolution={canEditResolution}
            deferSave={showEditFooter}
          />
        ) : (
          <PackageItemForm
            packageItems={packageItems}
            setPackageItems={setPackageItems}
            editingPackageItem={editingPackageItem}
            closeModal={closeModal}
            canSellItems={editMode == 'enabled'}
            movilLink={movil_link}
          />
        )}
      </Modal>
    </div>
  );
};

export default PackageItemsList;
